import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigation } from "react-router";

import { messages } from "@/localization/Shared.messages";

const COMPLETE_PROGRESS = 100;
const COMPLETION_FADE_MS = 400;
const FADE_IN_MS = 200;
const INITIAL_PROGRESS = 12;
const MAX_NAVIGATING_PROGRESS = 99.4;
const PROGRESS_TICK_MS = 200;
const WIDTH_TRANSITION_MS = 200;

type ProgressPhase = "hidden" | "starting" | "active" | "finishing";

function getProgressStep(progress: number): number {
  if (progress < 20) {
    return 10;
  }

  if (progress < 50) {
    return 4;
  }

  if (progress < 80) {
    return 2;
  }

  if (progress < MAX_NAVIGATING_PROGRESS) {
    return 0.5;
  }

  return 0;
}

function getOpacity(phase: ProgressPhase): number {
  return phase === "active" ? 1 : 0;
}

function getTransition(phase: ProgressPhase): string {
  if (phase === "starting") {
    return "none";
  }

  const opacityDuration = phase === "finishing" ? COMPLETION_FADE_MS : FADE_IN_MS;
  return `width ${WIDTH_TRANSITION_MS}ms ease-out, opacity ${opacityDuration}ms ease-out`;
}

export default function RouterNavigationProgressBar() {
  const intl = useIntl();
  const navigation = useNavigation();
  const isNavigating = navigation.state !== "idle";
  const [phase, setPhase] = useState<ProgressPhase>("hidden");
  const [progress, setProgress] = useState(0);
  const hasStartedRef = useRef(false);

  /* eslint-disable react-hooks/set-state-in-effect -- progress state intentionally follows router navigation lifecycle. */
  useEffect(() => {
    if (isNavigating) {
      hasStartedRef.current = true;
      setPhase("starting");
      setProgress(0);

      const frameId = window.requestAnimationFrame(() => {
        setPhase("active");
        setProgress(INITIAL_PROGRESS);
      });

      const intervalId = window.setInterval(() => {
        setProgress((currentProgress) => {
          if (currentProgress >= MAX_NAVIGATING_PROGRESS) {
            return currentProgress;
          }

          return Math.min(
            currentProgress + getProgressStep(currentProgress),
            MAX_NAVIGATING_PROGRESS,
          );
        });
      }, PROGRESS_TICK_MS);

      return () => {
        window.cancelAnimationFrame(frameId);
        window.clearInterval(intervalId);
      };
    }

    if (!hasStartedRef.current) {
      return;
    }

    setPhase("finishing");
    setProgress(COMPLETE_PROGRESS);

    const timeoutId = window.setTimeout(() => {
      hasStartedRef.current = false;
      setPhase("hidden");
      setProgress(0);
    }, COMPLETION_FADE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isNavigating]);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (phase === "hidden") {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 h-[2px]">
      <div
        role="progressbar"
        aria-label={intl.formatMessage(messages.pageNavigationProgress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
        className="h-full bg-blue-200"
        style={{
          opacity: getOpacity(phase),
          transition: getTransition(phase),
          width: `${progress}%`,
        }}
      />
    </div>
  );
}
